import React, { useState, useEffect, useRef } from "react";
import "./Pages.css";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useBusinessQuery from "../../store/Bussiness/useBusinessQuery";
import useServiceQuery from "../../store/Service/useServiceQuery";

const EditInfo = () => {
  const history = useHistory();

  const [businessId, setBusinessId] = useState(null);
  const [aboutUs, setAboutUs] = useState("");
  const [services, setServices] = useState([]);
  const [savingAbout, setSavingAbout] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  // Track whether we have already seeded the form from the API
  // so a later refetch doesn't overwrite the customer's in-progress edits.
  const aboutSeeded = useRef(false);
  const servicesSeeded = useRef(false);

  // ── Step 1: get the list to obtain the business id ──
  const { data: bizList, isSuccess: bizListSuccess } =
    useBusinessQuery.Get_my_bussiness();

  // ── Step 2: get the DETAIL record (has full about_us) ──
  const { data: bizDetail, isSuccess: bizDetailSuccess } =
    useBusinessQuery.Get_my_bussiness_details(businessId);

  // ── Step 3: get the PUBLIC detail record — same source as ProductDetails page
  // This endpoint returns data.services as the authoritative list (same data
  // the public page displays), regardless of which panel entered them.
  const publicBusinessId = bizList?.data?.results?.[0]?.id ?? null;
  const { data: publicDetail, isSuccess: publicDetailSuccess } =
    useBusinessQuery.Get_details(publicBusinessId);

  // Also keep the writable services endpoint for add/edit/delete mutations
  const { data: serviceData, isSuccess: serviceSuccess } =
    useServiceQuery.Get_service(businessId);

  const { mutateAsync: editBusiness } = useBusinessQuery.Edit_business();
  const { mutateAsync: addService } = useServiceQuery.Add_service();
  const { mutateAsync: editService } = useServiceQuery.Edit_service();
  const { mutateAsync: deleteService } = useServiceQuery.Del_service();

  // ── Extract businessId from list ──
  useEffect(() => {
    if (bizListSuccess && bizList?.data?.results?.length > 0) {
      setBusinessId(bizList.data.results[0].id);
    }
  }, [bizListSuccess, bizList]);

  // ── Seed about_us from DETAIL endpoint (full record) ──
  useEffect(() => {
    if (bizDetailSuccess && !aboutSeeded.current) {
      // getBusinessDetails returns data?.data (axios body) so bizDetail IS the backend object.
      // Handle both { data: { about_us } } and { about_us } shapes.
      const record = bizDetail?.data ?? bizDetail;
      if (record) {
        setAboutUs(record.about_us || "");
        aboutSeeded.current = true;
      }
    }
  }, [bizDetailSuccess, bizDetail]);

  // ── Seed services ──
  // Reset the seeded flag whenever businessId changes so we re-seed for the
  // correct business after the ID is resolved from the list query.
  useEffect(() => {
    servicesSeeded.current = false;
  }, [businessId]);

  useEffect(() => {
    if (servicesSeeded.current) return;

    // Primary: /business/my_business/{id}/services/
    // getService returns data?.data (axios body), so serviceData IS the backend body.
    // Backend shape: { results: [...] } or { data: { results: [...] } } — handle both.
    const primaryResults =
      serviceData?.data?.results ?? serviceData?.results ?? null;

    if (serviceSuccess && Array.isArray(primaryResults) && primaryResults.length > 0) {
      // Got real services from the writable endpoint — use them.
      setServices(
        primaryResults.map((s) => ({
          id: s.id,
          name: s.name,
          editing: false,
          originalName: s.name,
        }))
      );
      servicesSeeded.current = true;
      return;
    }

    // Fallback: /business/{id}/detail/ — same source as the public page.
    // Backend shape: { data: { services: [...] } } OR { services: [...] }.
    const fallbackServices =
      publicDetail?.data?.services ?? publicDetail?.services ?? null;

    if (publicDetailSuccess && Array.isArray(fallbackServices)) {
      // Seed from public detail (may be empty — that is a valid confirmed state).
      setServices(
        fallbackServices.map((s) => ({
          id: s.id,
          name: s.name,
          editing: false,
          originalName: s.name,
        }))
      );
      servicesSeeded.current = true;
    }
  }, [serviceSuccess, serviceData, publicDetailSuccess, publicDetail, businessId]);

  // ── Service actions ──
  const addNewService = () => {
    setServices((prev) => [
      ...prev,
      { id: null, name: "", editing: true, originalName: "" },
    ]);
  };

  const updateName = (idx, name) => {
    setServices((prev) => prev.map((s, i) => (i === idx ? { ...s, name } : s)));
  };

  const confirmService = async (idx) => {
    const svc = services[idx];
    const trimmed = svc.name.trim();
    if (!trimmed) return;
    setServiceError(null);
    try {
      if (!svc.id) {
        // CREATE — new service with no persisted id yet
        const res = await addService({ id: businessId, name: trimmed, is_active: true });
        const newId = res?.data?.id ?? res?.id ?? null;
        setServices((prev) =>
          prev.map((s, i) =>
            i === idx
              ? { ...s, id: newId, name: trimmed, editing: false, originalName: trimmed }
              : s
          )
        );
      } else {
        // UPDATE — PATCH the existing record by its id; never creates a duplicate
        const res = await editService({ id: svc.id, business_id: businessId, name: trimmed, is_active: true });
        // Use the server-returned name if available, otherwise use what we sent
        const savedName = res?.data?.name ?? res?.name ?? trimmed;
        setServices((prev) =>
          prev.map((s, i) =>
            i === idx
              ? { ...s, name: savedName, editing: false, originalName: savedName }
              : s
          )
        );
      }
    } catch (e) {
      // Keep edit mode open and show error — do NOT close or overwrite saved data
      console.error(e);
      setServiceError("Failed to save. Please try again.");
    }
  };

  const startEdit = (idx) => {
    setServices((prev) => prev.map((s, i) => (i === idx ? { ...s, editing: true } : s)));
  };

  const cancelEdit = (idx) => {
    const svc = services[idx];
    if (!svc.id) {
      setServices((prev) => prev.filter((_, i) => i !== idx));
    } else {
      setServices((prev) =>
        prev.map((s, i) =>
          i === idx ? { ...s, name: s.originalName, editing: false } : s
        )
      );
    }
  };

  const handleDelete = async (idx) => {
    const svc = services[idx];
    if (!svc.id) {
      setServices((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    try {
      await deleteService({ business_id: businessId, id: svc.id });
      setServices((prev) => prev.filter((_, i) => i !== idx));
    } catch (e) {
      console.error(e);
    }
  };

  const persistPendingServices = async () => {
    const pending = services
      .map((svc, idx) => ({ svc, idx }))
      .filter(({ svc }) => svc.editing && (svc.name || "").trim());

    for (const { idx, svc } of pending) {
      const trimmed = svc.name.trim();

      try {
        if (!svc.id) {
          const res = await addService({ id: businessId, name: trimmed, is_active: true });
          const newId = res?.data?.id ?? res?.id ?? null;
          setServices((prev) =>
            prev.map((item, i) =>
              i === idx
                ? { ...item, id: newId, name: trimmed, editing: false, originalName: trimmed }
                : item
            )
          );
        } else {
          const res = await editService({ id: svc.id, business_id: businessId, name: trimmed, is_active: true });
          const savedName = res?.data?.name ?? res?.name ?? trimmed;
          setServices((prev) =>
            prev.map((item, i) =>
              i === idx
                ? { ...item, name: savedName, editing: false, originalName: savedName }
                : item
            )
          );
        }
      } catch (e) {
        console.error(e);
        setServiceError("Failed to save one or more services. Please try again.");
        throw e;
      }
    }
  };

  // ── Save about_us and any pending service edits ──
  const handleSaveAbout = async () => {
    if (!businessId) return;
    setSavingAbout(true);
    try {
      await persistPendingServices();
      // getBusinessDetails returns data?.data so bizDetail IS the backend object.
      // Handle both { data: {...} } and flat { id, about_us, ... } shapes.
      const biz = bizDetail?.data ?? bizDetail ?? bizList?.data?.results?.[0];
      const payload = { id: businessId, about_us: aboutUs };
      const carry = [
        "name", "mobile_number", "type", "category", "sub_category",
        "district", "post_office", "pin_code", "state", "latitude", "longitude",
        "image", "banner_image", "whatsapp_number", "facebook", "instagram",
        "youtube", "website", "location_info", "google_location_link",
        "google_review_link",
      ];
      carry.forEach((k) => { if (biz?.[k] != null) payload[k] = biz[k]; });
      await editBusiness(payload);
      setServiceError(null);
      // Allow re-seeding on next fetch after save
      aboutSeeded.current = false;
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2800);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAbout(false);
    }
  };

  const loading = !bizListSuccess || (businessId && (!bizDetailSuccess || !publicDetailSuccess));

  return (
    <div className="pg-root">
      <div className="pg-topbar">
        <ArrowBackIcon
          onClick={() => history.push("/my-profile")}
          sx={{ color: "#0E3A53", cursor: "pointer", fontSize: 22 }}
        />
        <Typography className="pg-topbar-title">About & Services</Typography>
      </div>

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <CircularProgress size={32} sx={{ color: "#2182B1" }} />
        </div>
      ) : (
        <div className="pg-body">

          {/* ── About ── */}
          <div className="pg-card">
            <Typography className="pg-card-title">
              <InfoOutlinedIcon /> About Business
            </Typography>
            <div className="pg-field">
              <Typography className="pg-label" style={{ color: "#0b2e59" }}>About</Typography>
              <div className="pg-textarea-wrap">
                <textarea
                  className="pg-textarea"
                  placeholder="Tell customers about your business…"
                  value={aboutUs}
                  rows={5}
                  onChange={(e) => setAboutUs(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Services ── */}
          <div className="pg-card">
            <Typography className="pg-card-title">
              <BuildOutlinedIcon /> Services
            </Typography>

            {services.length === 0 && (
              <Typography sx={{ fontSize: "13px", color: "#a0bfce", textAlign: "center", py: 1 }}>
                No services added yet. Tap "Add Service" to begin.
              </Typography>
            )}

            {serviceError && (
              <Typography sx={{ fontSize: "12px", color: "#e53935", mb: 1, px: 0.5 }}>
                {serviceError}
              </Typography>
            )}

            {services.map((svc, idx) => (
              <div key={svc.id ?? `new-${idx}`} className="pg-service-row">
                {svc.editing ? (
                  <div className="pg-service-edit-row">
                    <span className="pg-service-num">{idx + 1}</span>
                    <input
                      className="pg-service-input"
                      placeholder={`Service ${idx + 1}`}
                      value={svc.name}
                      autoFocus
                      onChange={(e) => updateName(idx, e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && confirmService(idx)}
                    />
                    <Button
                      className="pg-svc-btn"
                      onClick={() => confirmService(idx)}
                      sx={{ background: "#ecfdf5 !important" }}
                    >
                      <CheckIcon sx={{ fontSize: 15, color: "#10b981" }} />
                    </Button>
                    <Button
                      className="pg-svc-btn"
                      onClick={() => cancelEdit(idx)}
                      sx={{ background: "#f3f4f6 !important" }}
                    >
                      <CloseIcon sx={{ fontSize: 15, color: "#6b7280" }} />
                    </Button>
                  </div>
                ) : (
                  <div className="pg-service-display-row">
                    <span className="pg-service-num">{idx + 1}</span>
                    <span className="pg-service-text">{svc.name}</span>
                    <Button
                      className="pg-svc-btn"
                      onClick={() => startEdit(idx)}
                      sx={{ background: "#e8f4f8 !important" }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 15, color: "#2182B1" }} />
                    </Button>
                    <Button
                      className="pg-svc-btn"
                      onClick={() => handleDelete(idx)}
                      sx={{ background: "#fff0f0 !important" }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 15, color: "#e53935" }} />
                    </Button>
                  </div>
                )}
              </div>
            ))}

            <Button className="pg-add-btn" onClick={addNewService} sx={{ mt: "12px !important" }}>
              <AddIcon /> Add Service
            </Button>
          </div>

        </div>
      )}

      <div className="pg-save-bar">
        <Button className="pg-cancel-btn" onClick={() => history.push("/my-profile")}>
          Cancel
        </Button>
        <Button
          className="pg-save-btn"
          onClick={handleSaveAbout}
          disabled={savingAbout || loading}
          startIcon={savingAbout ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
        >
          {savingAbout ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      {showToast && (
        <div className="pg-toast">
          <CheckCircleIcon sx={{ fontSize: 16 }} /> Saved successfully
        </div>
      )}
    </div>
  );
};

export default EditInfo;
